/**
 * Icons.
 *
 * Drawn here, inline, as SVG. No icon font, no sprite sheet, no dependency —
 * and no emoji, which render differently on every operating system and look
 * like a placeholder that was never replaced.
 *
 * Every path is authored on the same 16-unit grid at the same stroke weight
 * (`--icon-stroke`), with round caps and joins, so a row of them reads as one
 * set rather than a collection. Size comes from `--icon-sm/md/lg/xl`; colour is
 * always `currentColor`, so an icon inherits the tone of the text it labels and
 * a status glyph can never disagree with its label.
 *
 * The severity glyphs at the bottom carry the accessibility rule from
 * `docs/06-DESIGN-SYSTEM.md`: colour never means anything on its own. Each
 * severity has a DIFFERENT OUTLINE — disc, ring, diamond, triangle, bar — so
 * the four states are told apart with the colour removed entirely.
 */
import type { ReactNode } from 'react';

export type IconSize = 'sm' | 'md' | 'lg' | 'xl';

interface IconProps {
  size?: IconSize;
  className?: string;
  title?: string;
}

function Svg({
  size = 'md',
  className,
  title,
  children,
}: IconProps & { children: ReactNode }): ReactNode {
  return (
    <svg
      className={`icon icon--${size}${className ? ` ${className}` : ''}`}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden={title ? undefined : true}
      role={title ? 'img' : undefined}
      focusable="false"
    >
      {title ? <title>{title}</title> : null}
      {children}
    </svg>
  );
}

// ------------------------------------------------------------- navigation

/** Dashboard. A dial, because the screen is a reading rather than a list. */
export function IconGauge(props: IconProps): ReactNode {
  return (
    <Svg {...props}>
      <path d="M2.25 11.5a5.75 5.75 0 1 1 11.5 0" />
      <path d="M8 11.5 11.1 6.9" />
      <circle cx="8" cy="11.5" r="1.05" fill="currentColor" stroke="none" />
      <path d="M2.4 9.4h1.3M12.3 9.4h1.3M8 5.4v1.2" />
    </Svg>
  );
}

/** Deposits — money arriving into a tray. */
export function IconInbound(props: IconProps): ReactNode {
  return (
    <Svg {...props}>
      <path d="M8 2.25v6.5" />
      <path d="M5.4 6.2 8 8.8l2.6-2.6" />
      <path d="M2.5 10.75v1.25a1.75 1.75 0 0 0 1.75 1.75h7.5a1.75 1.75 0 0 0 1.75-1.75v-1.25" />
    </Svg>
  );
}

/** Withdrawals — money leaving the tray. Same tray, reversed arrow. */
export function IconOutbound(props: IconProps): ReactNode {
  return (
    <Svg {...props}>
      <path d="M8 8.75v-6.5" />
      <path d="M5.4 4.85 8 2.25l2.6 2.6" />
      <path d="M2.5 10.75v1.25a1.75 1.75 0 0 0 1.75 1.75h7.5a1.75 1.75 0 0 0 1.75-1.75v-1.25" />
    </Svg>
  );
}

/** Players. */
export function IconPeople(props: IconProps): ReactNode {
  return (
    <Svg {...props}>
      <circle cx="6.1" cy="5.4" r="2.3" />
      <path d="M1.9 13.4c0-2.25 1.88-3.8 4.2-3.8s4.2 1.55 4.2 3.8" />
      <path d="M10.9 3.7a2.3 2.3 0 0 1 0 3.4" />
      <path d="M11.7 9.9c1.5.52 2.4 1.78 2.4 3.5" />
    </Svg>
  );
}

/** Games — a die on the same grid as everything else. */
export function IconDie(props: IconProps): ReactNode {
  return (
    <Svg {...props}>
      <rect x="2.4" y="2.4" width="11.2" height="11.2" rx="2.6" />
      <circle cx="5.7" cy="5.7" r="0.95" fill="currentColor" stroke="none" />
      <circle cx="8" cy="8" r="0.95" fill="currentColor" stroke="none" />
      <circle cx="10.3" cy="10.3" r="0.95" fill="currentColor" stroke="none" />
    </Svg>
  );
}

/** Payment methods — the collection accounts. */
export function IconVault(props: IconProps): ReactNode {
  return (
    <Svg {...props}>
      <path d="M1.9 6.4 8 2.4l6.1 4" />
      <path d="M3.6 6.9v5.4M6.5 6.9v5.4M9.5 6.9v5.4M12.4 6.9v5.4" />
      <path d="M2.2 13.6h11.6" />
    </Svg>
  );
}

// ------------------------------------------------------------------ action

export function IconRefresh(props: IconProps): ReactNode {
  return (
    <Svg {...props}>
      <path d="M13.4 8a5.4 5.4 0 1 1-1.72-3.95" />
      <path d="M13.6 2.3v3.2h-3.2" />
    </Svg>
  );
}

export function IconSearch(props: IconProps): ReactNode {
  return (
    <Svg {...props}>
      <circle cx="7" cy="7" r="4.3" />
      <path d="m10.2 10.2 3.4 3.4" />
    </Svg>
  );
}

export function IconSliders(props: IconProps): ReactNode {
  return (
    <Svg {...props}>
      <path d="M2.4 5.2h11.2M2.4 10.8h11.2" />
      <circle cx="6" cy="5.2" r="1.8" fill="var(--surface-raised)" />
      <circle cx="10.4" cy="10.8" r="1.8" fill="var(--surface-raised)" />
    </Svg>
  );
}

export function IconLock(props: IconProps): ReactNode {
  return (
    <Svg {...props}>
      <rect x="3.3" y="7" width="9.4" height="6.6" rx="1.8" />
      <path d="M5.6 7V5.3a2.4 2.4 0 0 1 4.8 0V7" />
      <circle cx="8" cy="10.3" r="0.9" fill="currentColor" stroke="none" />
    </Svg>
  );
}

export function IconChevron(props: IconProps): ReactNode {
  return (
    <Svg {...props}>
      <path d="m6.2 3.4 4.4 4.6-4.4 4.6" />
    </Svg>
  );
}

export function IconTrend(props: IconProps): ReactNode {
  return (
    <Svg {...props}>
      <path d="M1.9 11.6 5.9 7l2.4 2.4L14.1 4" />
      <path d="M10.6 4h3.5v3.5" />
    </Svg>
  );
}

export function IconArrowUp(props: IconProps): ReactNode {
  return (
    <Svg {...props}>
      <path d="M8 13.2V3.2" />
      <path d="m4.3 6.9 3.7-3.7 3.7 3.7" />
    </Svg>
  );
}

export function IconArrowDown(props: IconProps): ReactNode {
  return (
    <Svg {...props}>
      <path d="M8 2.8v10" />
      <path d="m4.3 9.1 3.7 3.7 3.7-3.7" />
    </Svg>
  );
}

/** Nothing here. Used by the designed empty state, never a shrug emoji. */
export function IconEmptyTray(props: IconProps): ReactNode {
  return (
    <Svg {...props}>
      <path d="M1.9 9.3 4.2 3.5a1.6 1.6 0 0 1 1.5-1h4.6a1.6 1.6 0 0 1 1.5 1l2.3 5.8" />
      <path d="M1.9 9.3h3.5l.9 1.8h3.4l.9-1.8h3.5v2.6a1.7 1.7 0 0 1-1.7 1.7H3.6a1.7 1.7 0 0 1-1.7-1.7z" />
    </Svg>
  );
}

/** A live reading. Used on loading states and the live-data chip. */
export function IconPulse(props: IconProps): ReactNode {
  return (
    <Svg {...props}>
      <path d="M1.6 8h2.9l1.9-4.4 3 8.8 1.9-4.4h3.1" />
    </Svg>
  );
}

export function IconPlug(props: IconProps): ReactNode {
  return (
    <Svg {...props}>
      <path d="M6 1.9v3.3M10 1.9v3.3" />
      <path d="M3.9 5.2h8.2v2.3a4.1 4.1 0 0 1-4.1 4.1 4.1 4.1 0 0 1-4.1-4.1z" />
      <path d="M8 11.6v2.5" />
    </Svg>
  );
}

// -------------------------------------------------------------- severity
//
// Five distinct outlines. With every colour stripped out these are still five
// different shapes, which is the whole requirement: red and green are the most
// common colour-vision deficiency and this panel releases money.

/** Good / settled / healthy — a filled disc with a check. */
export function IconCheck(props: IconProps): ReactNode {
  return (
    <Svg {...props}>
      <circle cx="8" cy="8" r="5.9" />
      <path d="m5.4 8.2 1.9 1.9 3.6-4.1" />
    </Svg>
  );
}

/** Waiting — a ring with hands. Distinct outline from the check disc. */
export function IconClock(props: IconProps): ReactNode {
  return (
    <Svg {...props}>
      <circle cx="8" cy="8" r="5.9" />
      <path d="M8 4.7V8l2.3 1.5" />
    </Svg>
  );
}

/** Informational / in progress — a diamond, no circle anywhere in it. */
export function IconDiamond(props: IconProps): ReactNode {
  return (
    <Svg {...props}>
      <path d="M8 2.1 13.9 8 8 13.9 2.1 8z" />
      <path d="M8 5.8v2.6" />
      <circle cx="8" cy="10.7" r="0.75" fill="currentColor" stroke="none" />
    </Svg>
  );
}

/** Wrong — a triangle. The only three-sided shape in the set. */
export function IconAlert(props: IconProps): ReactNode {
  return (
    <Svg {...props}>
      <path d="M8 2.2 14.4 13.5a0.8 0.8 0 0 1-0.7 1.2H2.3a0.8 0.8 0 0 1-0.7-1.2z" />
      <path d="M8 6.4v3.1" />
      <circle cx="8" cy="11.7" r="0.78" fill="currentColor" stroke="none" />
    </Svg>
  );
}

/** Inert — a bar. Reads as "nothing to report" without reading as a state. */
export function IconDash(props: IconProps): ReactNode {
  return (
    <Svg {...props}>
      <circle cx="8" cy="8" r="5.9" strokeDasharray="2.6 2.2" />
      <path d="M5.6 8h4.8" />
    </Svg>
  );
}

/** Closed / rejected — a cross. */
export function IconCross(props: IconProps): ReactNode {
  return (
    <Svg {...props}>
      <circle cx="8" cy="8" r="5.9" />
      <path d="m6.1 6.1 3.8 3.8M9.9 6.1l-3.8 3.8" />
    </Svg>
  );
}

/** The temporary-access banner. A shield, not a padlock — different meaning. */
export function IconShield(props: IconProps): ReactNode {
  return (
    <Svg {...props}>
      <path d="M8 1.9 13.1 3.8v4.1c0 3.05-2.2 5.3-5.1 6.2-2.9-.9-5.1-3.15-5.1-6.2V3.8z" />
      <path d="M8 5.9v2.6" />
      <circle cx="8" cy="10.8" r="0.75" fill="currentColor" stroke="none" />
    </Svg>
  );
}

/** The brand mark. A chip seen edge-on: four notches on a ring. */
export function BrandMark(): ReactNode {
  return (
    <svg
      className="brand__mark"
      viewBox="0 0 32 32"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <circle cx="16" cy="16" r="12.4" />
      <circle cx="16" cy="16" r="6.1" />
      <path d="M16 3.6v4.1M16 24.3v4.1M3.6 16h4.1M24.3 16h4.1" />
      <path d="M7.2 7.2 10 10M25 7.2 22.2 10M7.2 24.8 10 22M25 24.8 22.2 22" />
    </svg>
  );
}
